import React, { useEffect, useMemo, useState } from "react";
import { Backdrop, CircularProgress } from '@mui/material';
import styles from "./registerevent.module.css";
import wordmark from "./registereventassets/sunbaewordmark.svg";
import payssamLogo from "./registereventassets/payssamlogo.svg";
import { drawReceiptImage, ReceiptData } from "./receiptcanvas";
import ReceiptViewer, { uploadReceiptImage } from "./receiptviewer";

//결제선생 영수증을 그대로 그려서 보여주는 페이지 (재원생 결제완료 알림톡의 [결제선생 영수증] 버튼 링크)
//링크 : peetsunbae.com/#/paymentreceipt?bill_id=xxx&code=xxx
const PaymentReceipt: React.FC<any> = (props) => {

    const { billId, codeParam } = useMemo(() => {
        const url = window.location.href;
        const query = url.split("?bill_id=")[1];

        if (!query) {
            return { billId: "", codeParam: "" };
        }

        return {
            billId: query.split("&code=")[0] || "",
            codeParam: query.split("&code=")[1] || ""
        };
    }, []);

    const [loading, setLoading] = useState(true);
    const [errorState, setErrorState] = useState<"" | "invalid" | "notPaid" | "error">("");
    const [apprState, setApprState] = useState("");
    const [receiptImg, setReceiptImg] = useState<{ dataUrl: string, blob: Blob | null } | null>(null);
    const [receiptUrl, setReceiptUrl] = useState<string | null>(null);
    const [viewerOpen, setViewerOpen] = useState(false);

    useEffect(() => {

        document.title = "수능선배 결제 영수증";

        const meta = document.createElement("meta");
        meta.name = "viewport";
        meta.content = "width=device-width, initial-scale=1";
        document.getElementsByTagName("head")[0].appendChild(meta);

        if (!billId || !codeParam || !/^\d{20}$/.test(billId)) {
            setErrorState("invalid");
            setLoading(false);
            return;
        }

        fetch("https://peetsunbae.com/api/payments/billreceipt", {
            method: "post",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ bill_id: billId, code: codeParam })
        }).then((response: any) => {
            response.json().then(async (result: any) => {

                if (result.message === "success" && result.data) {

                    try {
                        const img = await drawReceiptImage(result.data as ReceiptData, payssamLogo);
                        setReceiptImg(img);

                        //https 이미지 URL로 만들어두면 카톡 등 인앱 브라우저에서 길게 눌러 사진 저장 가능 (실패해도 dataURL로 동작)
                        if (img.blob) {
                            uploadReceiptImage(billId, codeParam, img.blob).then((url) => {
                                if (url) {
                                    setReceiptUrl(url);
                                }
                            });
                        }
                    } catch (e) {
                        console.log(e);
                        setErrorState("error");
                    }

                    setLoading(false);
                    return;
                }

                if (result.message === "notPaid" && result.data) {
                    setApprState(result.data.apprState || "");
                    setErrorState("notPaid");
                    setLoading(false);
                    return;
                }

                setErrorState("invalid");
                setLoading(false);

            }).catch(() => {
                setErrorState("error");
                setLoading(false);
            });
        }).catch(() => {
            setErrorState("error");
            setLoading(false);
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    //영수증 이미지 저장
    //1) 공유시트 지원 브라우저(사파리·크롬)는 공유창에서 '이미지 저장' -> 사진첩
    //2) PC는 파일 다운로드
    //3) 카톡 등 인앱 브라우저는 blob 다운로드가 안 되므로 크게 보기를 열어 "길게 눌러 이미지 저장" 유도
    const saveReceipt = async () => {

        if (!receiptImg) {
            return;
        }

        const nav: any = navigator;
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

        if (receiptImg.blob && nav.canShare) {
            try {
                const imageFile = new File([receiptImg.blob], "수능선배_영수증.png", { type: "image/png" });

                if (nav.canShare({ files: [imageFile] })) {
                    await nav.share({ files: [imageFile] });
                    return;
                }
            } catch (e: any) {
                if (e && e.name === "AbortError") {
                    //사용자가 공유창을 직접 닫음 - 추가 동작 없음
                    return;
                }
                //그 외 실패는 아래 폴백으로
            }
        }

        if (!isMobile && receiptImg.blob) {
            try {
                const url = URL.createObjectURL(receiptImg.blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "수능선배_영수증.png";
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 10000);
                return;
            } catch (e) {
                console.log(e);
            }
        }

        setViewerOpen(true);
    };

    const notPaidMessage = apprState === "C" ? "취소된 결제예요." : apprState === "D" ? "파기된 청구서예요." : "아직 결제가 완료되지 않았어요.";

    return (
        <div className={styles.stage}>
            <div className={styles.frame}>

                <header className={styles.header}>
                    <img src={wordmark} alt="수능선배" className={styles.headerLogo} />
                </header>

                {
                    errorState
                        ?
                        <div className={styles.notice}>
                            <p className={styles.noticeTitle}>
                                {errorState === "notPaid" ? "영수증을 표시할 수 없어요" : errorState === "invalid" ? "잘못된 접근이에요" : "일시적인 오류가 발생했어요"}
                            </p>
                            <p className={styles.noticeDesc}>
                                {
                                    errorState === "notPaid"
                                        ? notPaidMessage
                                        : errorState === "invalid"
                                            ? "알림톡의 [결제선생 영수증] 버튼으로 다시 접속해주세요."
                                            : "잠시 후 다시 시도해주세요.\n문제가 계속되면 카카오톡 채널로 문의해주세요."
                                }
                            </p>
                        </div>
                        :
                        <>
                            <div className={styles.eventHeader}>
                                <h2 className={styles.eventEyebrow}>결제 영수증</h2>
                                <hr className={styles.hair} />
                            </div>

                            <div className={styles.receiptPageBody}>
                                <div className={styles.receiptFull}>
                                    {receiptImg && <img src={receiptUrl || receiptImg.dataUrl} alt="결제 영수증" />}
                                </div>
                                <button type="button" className={styles.btn} disabled={!receiptImg} onClick={saveReceipt}>
                                    영수증 저장<span className={styles.btnArrow}>↓</span>
                                </button>
                                <p className={styles.hint}>공유 창이 뜨면 <b>‘이미지 저장’</b>을, 크게 보기가 열리면 이미지를 <b>길게 눌러 ‘이미지 저장’</b>을 선택해 주세요.</p>
                            </div>
                        </>
                }

                <ReceiptViewer
                    open={viewerOpen}
                    imgSrc={receiptUrl || (receiptImg ? receiptImg.dataUrl : "")}
                    onClose={() => setViewerOpen(false)}
                />

                <Backdrop
                    sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={loading}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
            </div>
        </div>
    );
};

export default PaymentReceipt;
